// frontend\src\components\GoogleButton.js
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import '../../src/style/Auth/GoogleButton.css';

const GoogleButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, refreshUserFromAPI } = useAuth(); // 🔥 Tambahkan refreshUserFromAPI
  const abortController = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  
  const [rememberMe, setRememberMe] = useState(true);

  useEffect(() => {
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  const isRegisterPage = location.pathname === '/register';

  const showErrorModal = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

const handleSuccess = async (credentialResponse) => {
  console.log('🔐 GOOGLE AUTH STARTED');
  
  abortController.current = new AbortController();

  try {
    const payload = {
      token: credentialResponse.credential,
      action: isRegisterPage ? 'register' : 'login',
      rememberMe: rememberMe
    };

    if (isRegisterPage) {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      if (refCode) {
        payload.referral_code = refCode;
      }
    }

    console.log('📤 Sending to backend...');
const res = await axios.post(
  `${process.env.REACT_APP_API_BASE_URL}/auth/google`,
  payload,
  {
    signal: abortController.current.signal,
    timeout: 30000,
    headers: { 
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    withCredentials: true
  }
);

console.log('✅ Backend response received:', res.data);

// CEK APAKAH MEMERLUKAN APPROVAL
if (res.data?.requires_approval || res.data?.code === 'PENDING_APPROVAL') {
  console.log('⏳ Account pending approval:', res.data);
  
  toast.info(res.data.message || 'Your account is pending admin approval');
  
  navigate('/account-checking', { 
    state: { 
      email: res.data.email,
      isActive: false,
      message: res.data.message
    } 
  });
  
  return;
}

// Jika login sukses (token ada)
if (res.data?.token && res.data?.user) {
  console.log('🔐 Calling login function with rememberMe:', rememberMe);
  
  login(res.data.user, res.data.token, rememberMe);
  
  console.log('📍 Redirecting to dashboard...');
  
  // 🔥 TAMBAHKAN DELAY LEBIH LAMA DAN PASTIKAN REFRESH DIPANGGIL
  setTimeout(async () => {
    console.log('🔄 Refreshing user data after login (delayed)...');
    try {
      await refreshUserFromAPI();
      console.log('✅ Refresh completed');
    } catch (err) {
      console.error('❌ Refresh failed:', err);
    }
  }, 2000); // Delay 2 detik untuk memastikan login selesai
  
  if (res.data.user.role === 'admin') {
    navigate('/admin/dashboard', { replace: true });
  } else {
    navigate('/dashboard', { replace: true });
  }
} else {
  console.error('❌ Invalid response format from backend');
  toast.error('Authentication failed - invalid response');
}

} catch (err) {
  if (!axios.isCancel(err)) {
    console.error('💥 Google auth error DETAIL:', {
      message: err.message,
      name: err.name,
      code: err.code,
      response: err.response ? {
        status: err.response.status,
        statusText: err.response.statusText,
        data: err.response.data,
        headers: err.response.headers
      } : 'NO RESPONSE',
      request: err.request ? 'REQUEST EXISTS' : 'NO REQUEST',
      config: err.config ? {
        url: err.config.url,
        method: err.config.method,
        timeout: err.config.timeout
      } : null
    });

    if (err.message === 'Network Error' || err.code === 'ECONNABORTED') {
      toast.error('Cannot connect to server. Please check your connection.');
      showErrorModal('Network Error: Cannot connect to server');
      return;
    }

    const errorData = err.response?.data;
    
    if (errorData?.code === 'PENDING_APPROVAL') {
      console.log('⏳ Account pending approval (from error)');
      toast.info(errorData.message || 'Your account is pending admin approval');
      
      navigate('/account-checking', { 
        state: { 
          email: errorData.email,
          isActive: false,
          message: errorData.message
        } 
      });
      return;
    }

    if (errorData?.code === 'ACCOUNT_NOT_REGISTERED') {
      showErrorModal('Account not registered. Please register first.');
      toast.error('Account not registered. Please register first.');
      setTimeout(() => navigate('/register'), 2000);
      return;
    }

    const errorMessage = errorData?.error || err.message || 'Google authentication failed';
    toast.error(errorMessage);
    showErrorModal(errorMessage);
  }
}
  };

  return (
    <>
      <div className="google-auth-wrapper">
        {/* HIDDEN REMEMBERME - using default true */}
        {!isRegisterPage && (
          <div style={{ display: 'none' }}>
            <input 
              type="checkbox" 
              checked={rememberMe} 
              onChange={(e) => setRememberMe(e.target.checked)}
            />
          </div>
        )}
        
        <GoogleOAuthProvider 
          clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
          onScriptLoadError={() => {
            console.error('Failed to load Google script');
            toast.error('Failed to load Google authentication');
          }}
        >
          <GoogleLogin
            onSuccess={handleSuccess}
            onError={() => {
              console.error('Google login failed');
              toast.error('Google login failed');
            }}
            useOneTap={!isRegisterPage}
            auto_select={!isRegisterPage}
            text={isRegisterPage ? "signup_with" : "signin_with"}
            cancel_on_tap_outside={false}
            ux_mode="popup"
          />
        </GoogleOAuthProvider>
      </div>

      {showModal && (
        <div className="modal-overlay-gbutton">
          <div className="modal-content-gbutton">
            <div className="modal-header-gbutton">
              <h3>Authentication Notice</h3>
              <button className="modal-close-gbutton" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <p>{modalMessage}</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button" onClick={closeModal}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GoogleButton;